// Step7.js - WHS Issues (Enhanced with photo upload support)
import React from 'react';
import ModernDataTable from '../ui/ModernDataTable';

const Step7 = ({ 
  whsItems, 
  setWhsItems, 
  defaultDateTime,
  enablePhotoUpload,
  referenceNo,
  sectionName,
  dataTableRef
}) => {
  const whsFields = [
    {
      name: 'timeRaised',
      type: 'datetime',
      label: 'Time raised',
      header: 'Time raised',
      required: true,
      validationMessage: 'Please enter when the WHS issue was first identified',
      hint: 'Record the exact time when the Workplace Health & Safety issue was discovered or first reported. This is critical for WHS compliance reporting.'
    },
    {
      name: 'whsCategory',
      type: 'dropdown',
      label: 'WHS issue category',
      header: 'WHS category',
      placeholder: 'Select the type of WHS issue',
      options: [
        { value: 'Slip, Trip or Fall Hazard', label: 'Slip, Trip or Fall Hazard' },
        { value: 'Manual Handling Issue', label: 'Manual Handling Issue' },
        { value: 'Chemical or Hazardous Substance', label: 'Chemical or Hazardous Substance' },
        { value: 'Equipment Safety Issue', label: 'Equipment Safety Issue' },
        { value: 'Fire Safety Concern', label: 'Fire Safety Concern' },
        { value: 'Emergency Access/Egress Issue', label: 'Emergency Access/Egress Issue' },
        { value: 'Personal Injury Incident', label: 'Personal Injury Incident' },
        { value: 'Near Miss Event', label: 'Near Miss Event' },
        { value: 'Ergonomic/Workplace Setup', label: 'Ergonomic/Workplace Setup' },
        { value: 'Environmental Health Issue', label: 'Environmental Health Issue' },
        { value: 'Other WHS Issue', label: 'Other WHS Issue' }
      ],
      required: true,
      validationMessage: 'Please select the appropriate WHS issue category from the dropdown',
      hint: 'Choose the category that best describes the type of workplace health and safety issue encountered.'
    },
    {
      name: 'location',
      type: 'text',
      label: 'Location',
      header: 'Location',
      placeholder: 'Specific location where the WHS issue occurred',
      required: true,
      validationMessage: 'Please specify the exact location where the WHS issue occurred',
      hint: 'Provide precise location details including level, section, room number, and any relevant landmarks for WHS investigation purposes.'
    },
    {
      name: 'description',
      type: 'textarea',
      label: 'Describe the issue',
      header: 'Description of issue',
      placeholder: 'Describe the WHS issue that occurred...',
      required: true,
      validationMessage: 'Please provide a comprehensive description of the WHS issue',
      hint: (
        <div>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>Provide detailed information about:</p>
          <ul style={{ margin: 0, paddingLeft: '1rem' }}>
            <li>Specific nature of the WHS hazard or incident</li>
            <li>Contributing factors or root causes</li>
            <li>Actual or potential severity of harm</li>
            <li>Environmental conditions at the time</li>
            <li>Any equipment or substances involved</li>
            <li>Witnesses present (if applicable)</li>
          </ul>
          <p style={{ margin: '0.5rem 0 0 0', fontStyle: 'italic' }}>This information is required for WHS compliance and incident investigation.</p>
        </div>
      )
    },
    {
      name: 'severityLevel',
      type: 'dropdown',
      label: 'Severity level',
      header: 'Severity',
      placeholder: 'Assess the severity of this WHS issue',
      options: [
        { value: 'Low - Minor risk, no immediate danger', label: 'Low - Minor risk, no immediate danger' },
        { value: 'Medium - Moderate risk, potential for harm', label: 'Medium - Moderate risk, potential for harm' },
        { value: 'High - Significant risk, likely harm', label: 'High - Significant risk, likely harm' },
        { value: 'Critical - Imminent danger, severe harm possible', label: 'Critical - Imminent danger, severe harm possible' }
      ],
      required: true,
      validationMessage: 'Please assess and select the severity level of this WHS issue',
      hint: 'Assess the potential for harm based on the likelihood and consequence of injury or illness.'
    },
    {
      name: 'numberOfEmployees',
      type: 'number',
      label: 'Number of employees involved (if relevant)',
      header: 'Employees involved',
      placeholder: 'Enter number of staff affected or involved',
      min: 0,
      max: 100,
      validationMessage: 'Please enter the number of employees involved (0-100)',
      hint: 'Enter the number of employees who were directly affected by, involved in, or witnessed this WHS issue. Enter 0 if no employees were directly involved.',
      displayTransform: (value) => value > 0 ? `${value} employees` : 'None'
    },
    {
      name: 'employeeDetails',
      type: 'textarea',
      label: 'Document employee details and impacts',
      header: 'Employee details',
      placeholder: 'Provide employee full name, employee ID, and description of impact for each employee involved...',
      hideUntil: {
        condition: (data) => parseInt(data.numberOfEmployees) > 0
      },
      conditionalRequired: {
        condition: (data) => parseInt(data.numberOfEmployees) > 0,
        message: 'Please provide details for all employees involved in this WHS issue (required for compliance)'
      },
      hint: (
        <div>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>For each employee involved, document:</p>
          <ul style={{ margin: 0, paddingLeft: '1rem' }}>
            <li>Full name and employee ID</li>
            <li>Role/position at time of incident</li>
            <li>Nature of involvement (injured, witness, etc.)</li>
            <li>Description of any injury or impact</li>
            <li>First aid or medical treatment provided</li>
          </ul>
          <p style={{ margin: '0.5rem 0 0 0', fontStyle: 'italic' }}>This information is mandatory for WHS incident reporting.</p>
        </div>
      )
    },
    {
      name: 'actionTaken',
      type: 'textarea',
      label: 'Immediate action taken',
      header: 'Action taken',
      placeholder: 'Describe immediate actions taken to address the WHS issue...',
      required: false,
      validationMessage: 'Please describe the immediate actions taken to address this WHS issue',
      hint: (
        <div>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>Document immediate response actions including:</p>
          <ul style={{ margin: 0, paddingLeft: '1rem' }}>
            <li>First aid or medical assistance provided</li>
            <li>Hazard isolation or area securing</li>
            <li>Emergency services contacted (if applicable)</li>
            <li>Witnesses identified and interviewed</li>
            <li>Evidence preservation or photography</li>
            <li>Supervisor or WHS officer notification</li>
            <li>Temporary controls implemented</li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <ModernDataTable
      suppressTitle={true}
      data={whsItems}
      setData={setWhsItems}
      fields={whsFields}
      addButtonText="Add WHS Issue"
      emptyMessage="No WHS issues added yet."
      truncateLines={2}
      defaultDateTime={defaultDateTime}
      enablePhotoUpload={enablePhotoUpload}
      referenceNo={referenceNo}
      sectionName={sectionName}
      ref={dataTableRef}
    />
  );
};

export default Step7;